import { useState } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";

const PUBLIC_PRIVATE_OPTIONS = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
].map((state) => ({ value: state, label: state }));

function AirportForm({ onSubmit, onCancel, initialData = null, loading = false }) {
  const [formData, setFormData] = useState({
    airportName: initialData?.airportName || "",
    address: initialData?.address || "",
    city: initialData?.city || "",
    state: initialData?.state || "",
    // EXTRA FIELDS NOT IN SCHEMA - Commented out
    // locationId: "", // Not in airportInput schema
    managerName: initialData?.managerName || "",
    managerPhone: initialData?.managerPhone || "",
    airportPhone: initialData?.airportPhone || "",
    airportUrl: initialData?.airportUrl || "",
    airportEmail: initialData?.airportEmail || "",
    airportUse: initialData?.airportUse || "",
    airportOwnership: initialData?.airportOwnership || "",
    status: initialData?.status || "active",
    // openToSupportMissions: false, // Not in airportInput schema
    // fssPhoneNumber: "", // Not in airportInput schema
  });

  const [errors, setErrors] = useState({});

  // Phone number validation function - US format: (123) 123-1234
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) return null;
    // Remove all non-digit characters and check length
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "Phone number must be exactly 10 digits in format (123) 123-1234";
    }
    // Check if the format matches (XXX) XXX-XXXX
    const usPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!usPhoneRegex.test(phone.trim())) {
      return "Phone number must be in format (123) 123-1234";
    }
    return null;
  };

  // Phone number formatting function - US format: (123) 123-1234
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");
    // Limit to exactly 10 digits for US format
    const limitedDigits = digitsOnly.slice(0, 10);
    
    // Format as (XXX) XXX-XXXX for US numbers (exactly 10 digits)
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  };

  const handleChange = (field, value) => {
    // Format phone numbers as user types
    if (field === "airportPhone" || field === "managerPhone") {
      value = formatPhoneNumber(value);
    }

    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields according to schema (airportInput)
    // name: String! - required
    if (!formData.airportName.trim()) {
      newErrors.airportName = "Airport name is required";
    }

    // phone: String (optional)
    if (formData.airportPhone.trim()) {
      const phoneError = validatePhoneNumber(formData.airportPhone);
      if (phoneError) {
        newErrors.airportPhone = phoneError;
      }
    }

    // Validate manager phone if provided
    if (formData.managerPhone && formData.managerPhone.trim()) {
      const managerPhoneError = validatePhoneNumber(formData.managerPhone);
      if (managerPhoneError) {
        newErrors.managerPhone = managerPhoneError;
      }
    }

    // address: String - optional

    // airportUse: String - required
    if (!formData.airportUse || !formData.airportUse.trim()) {
      newErrors.airportUse = "Airport use is required";
    }

    // airportOwnership: String - required
    if (!formData.airportOwnership || !formData.airportOwnership.trim()) {
      newErrors.airportOwnership = "Airport ownership is required";
    }

    // city: String - required
    if (!formData.city || !formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // state: String - required
    if (!formData.state || !formData.state.trim()) {
      newErrors.state = "State is required";
    }

    // Optional fields - validate format if provided
    // managerName: String - optional
    // email: String - optional (but validate format if provided)
    if (
      formData.airportEmail &&
      formData.airportEmail.trim() &&
      !/\S+@\S+\.\S+/.test(formData.airportEmail)
    ) {
      newErrors.airportEmail = "Email is invalid";
    }

    // url: String - optional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <SoftBox>
      <Grid container spacing={1.5}>
        {/* Airport Name */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Name *
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Enter airport name"
            value={formData.airportName}
            onChange={(e) => handleChange("airportName", e.target.value)}
            error={!!errors.airportName}
          />
          {errors.airportName && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.airportName}
            </SoftTypography>
          )}
        </Grid>

        {/* Airport Use */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Use *
            </SoftTypography>
          </SoftBox>
          <SoftSelect
            placeholder="Select Use"
            options={PUBLIC_PRIVATE_OPTIONS}
            value={PUBLIC_PRIVATE_OPTIONS.find(
              (opt) => opt.value === formData.airportUse
            )}
            onChange={(option) =>
              handleChange("airportUse", option?.value || "")
            }
            error={!!errors.airportUse}
          />
          {errors.airportUse && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.airportUse}
            </SoftTypography>
          )}
        </Grid>

        {/* Airport Ownership */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Ownership *
            </SoftTypography>
          </SoftBox>
          <SoftSelect
            placeholder="Select Ownership"
            options={PUBLIC_PRIVATE_OPTIONS}
            value={PUBLIC_PRIVATE_OPTIONS.find(
              (opt) => opt.value === formData.airportOwnership
            )}
            onChange={(option) =>
              handleChange("airportOwnership", option?.value || "")
            }
            error={!!errors.airportOwnership}
          />
          {errors.airportOwnership && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.airportOwnership}
            </SoftTypography>
          )}
        </Grid>

        {/* Status */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Status
            </SoftTypography>
          </SoftBox>
          <SoftSelect
            placeholder="Select Status"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find(
              (opt) => opt.value === formData.status
            )}
            onChange={(option) => handleChange("status", option?.value || "active")}
          />
        </Grid>

        {/* Airport Phone */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Phone Number
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type="tel"
            placeholder="(XXX) XXX-XXXX"
            value={formData.airportPhone}
            onChange={(e) => handleChange("airportPhone", e.target.value)}
            error={!!errors.airportPhone}
          />
          {errors.airportPhone && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.airportPhone}
            </SoftTypography>
          )}
        </Grid>

        {/* Address Section */}
        <Grid item xs={12}>
          <SoftTypography variant="button" fontWeight="bold" mt={1.5} mb={1}>
            Address
          </SoftTypography>
        </Grid>

        {/* Airport Address */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Address
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Enter street address"
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
            error={!!errors.address}
          />
          {errors.address && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.address}
            </SoftTypography>
          )}
        </Grid>

        {/* City */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              City *
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Enter city"
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
            error={!!errors.city}
          />
          {errors.city && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.city}
            </SoftTypography>
          )}
        </Grid>

        {/* State */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              State *
            </SoftTypography>
          </SoftBox>
          <SoftSelect
            placeholder="Select State"
            options={US_STATES}
            value={US_STATES.find((opt) => opt.value === formData.state)}
            onChange={(option) => handleChange("state", option?.value || "")}
            error={!!errors.state}
          />
          {errors.state && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.state}
            </SoftTypography>
          )}
        </Grid>

        {/* Manager Information Section */}
        <Grid item xs={12}>
          <SoftTypography variant="button" fontWeight="bold" mt={1.5} mb={1}>
            Manager Information
          </SoftTypography>
        </Grid>

        {/* Airport Manager Name */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Manager Name
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Enter manager name"
            value={formData.managerName}
            onChange={(e) => handleChange("managerName", e.target.value)}
            error={!!errors.managerName}
          />
          {errors.managerName && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.managerName}
            </SoftTypography>
          )}
        </Grid>

        {/* Airport Manager Phone */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Manager Phone
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type="tel"
            placeholder="(XXX) XXX-XXXX"
            value={formData.managerPhone}
            onChange={(e) => handleChange("managerPhone", e.target.value)}
            error={!!errors.managerPhone}
          />
          {errors.managerPhone && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.managerPhone}
            </SoftTypography>
          )}
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={12}>
          <SoftTypography variant="button" fontWeight="bold" mt={1.5} mb={1}>
            Contact Information
          </SoftTypography>
        </Grid>

        {/* Airport Email */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Email
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type="email"
            placeholder="contact@airport.com"
            value={formData.airportEmail}
            onChange={(e) => handleChange("airportEmail", e.target.value)}
            error={!!errors.airportEmail}
          />
          {errors.airportEmail && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.airportEmail}
            </SoftTypography>
          )}
        </Grid>

        {/* Airport URL */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Website URL
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="https://www.airport.com"
            value={formData.airportUrl}
            onChange={(e) => handleChange("airportUrl", e.target.value)}
          />
        </Grid>

        {/* Airport Facility Type - EXTRA FIELD NOT IN SCHEMA */}
        {/* <Grid item xs={12} md={6}>
          <SoftBox mb={1}>
            <SoftTypography variant="caption" fontWeight="bold">
              Airport Facility Type
            </SoftTypography>
          </SoftBox>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.openToSupportMissions}
                onChange={(e) =>
                  handleChange("openToSupportMissions", e.target.checked)
                }
                color="info"
              />
            }
            label={
              <SoftTypography variant="button">
                Open to Support Missions
              </SoftTypography>
            }
          />
        </Grid> */}

        {/* Info Box */}
        {/* <Grid item xs={12}>
          <SoftBox
            bgColor="info"
            variant="gradient"
            borderRadius="lg"
            p={2}
            mt={2}
          >
            <SoftTypography variant="caption" color="white" fontWeight="bold">
              What happens next?
            </SoftTypography>
            <SoftTypography
              variant="caption"
              color="white"
              display="block"
              mt={1}
            >
              • Airport will be registered in the system
            </SoftTypography>
            <SoftTypography variant="caption" color="white" display="block">
              • Airport manager will receive welcome email
            </SoftTypography>
            <SoftTypography variant="caption" color="white" display="block">
              • Airport will be available for mission assignments
            </SoftTypography>
            {formData.openToSupportMissions && (
              <SoftTypography variant="caption" color="white" display="block">
                • Airport marked as open for support missions
              </SoftTypography>
            )}
          </SoftBox>
        </Grid> */}
      </Grid>

      {/* Action Buttons */}
      <SoftBox mt={1.5} display="flex" justifyContent="flex-end" gap={2}>
        <SoftButton variant="outlined" color="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="info"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
              Saving...
            </>
          ) : (
            <>
              <Icon sx={{ mr: 1 }}>save</Icon>
              {initialData ? "Update Airport" : "Create Airport"}
            </>
          )}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

export default AirportForm;
